/**
 * Provider dispatch for the Excel multi-hop executor, mirroring the PowerPoint
 * `services/ai/providers/index.ts` switch pattern.
 */

import type { AIProvider } from "../../shared/config/models";
import { AnthropicStreamError, anthropicExecutorAdapter } from "./anthropic";
import { openAiExecutorAdapter } from "./openai";
import type { ExecutorProviderAdapter } from "./types";

export function getExecutorAdapter(
  provider: AIProvider,
): ExecutorProviderAdapter {
  switch (provider) {
    case "openai":
      return openAiExecutorAdapter;
    case "anthropic":
      return anthropicExecutorAdapter;
    default:
      throw new Error(
        `No Excel executor adapter is registered for provider "${provider}"`,
      );
  }
}

/**
 * True when a thrown turn error means the provider will not return a usable
 * turn for this iteration: `max_tokens`, a refusal, a malformed `tool_use`, a
 * rejected request, or a stream still broken after its replay. The runner ends
 * the run through its partial-result path on these rather than letting the
 * exception escape, so workbook edits already applied survive.
 *
 * An abort never produces one of these errors: the caller signal is rethrown
 * from the adapter untouched so it can stop the run immediately. The OpenAI
 * rollback path also keeps its existing behavior of surfacing stream failures
 * to the caller.
 */
export function isFatalProviderTurnError(error: unknown): boolean {
  return error instanceof AnthropicStreamError;
}

export { FLAGGED_EXECUTOR_MODEL_ID, resolveExecutorModel } from "./registry";
export type {
  AgentToolCall,
  AgentTurn,
  AgentTurnResult,
  ExecutorStreamTurnParams,
} from "./types";
